/**
 * This is a collapse test with lazy-loaded children. It uses an opacity fade-in as well as
 * a MutationObserver (to assure that the expected number of children have already been
 * inserted before allowing the collapse to open) to help mitigate CLS issues from slow DOM
 * insertions.
 */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { noop } from 'lodash';
import Button from 'reactstrap/lib/Button';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { AnimatedCollapse } from 'client/site-modules/shared/utils/collapse-utils';

import './lazy-content-collapse.scss';

export function LazyContentCollapseTransitioned({
  trackingId,
  trackingValue,
  btnTrackingId,
  textOpen,
  textClosed,
  noAnim,
  children,
  className,
}) {
  const [canOpen, setCanOpen] = useState(false);
  const [childrenLoaded, setChildrenLoaded] = useState(false);
  const [isOpen, toggleOpen] = useToggle();
  const mutationTarget = useRef();

  useEffect(() => {
    if (canOpen) {
      return noop;
    }

    const observer = new MutationObserver(mutations => {
      // check if all children have been inserted
      if (mutations.length === children.length || (mutations.length === 1 && typeof children === 'object')) {
        setCanOpen(true);
        observer.disconnect();
      }
    });

    observer.observe(mutationTarget.current, { childList: true });

    return () => {
      observer.disconnect();
    };
  }, [children, canOpen]);

  function toggle(event) {
    event.preventDefault();

    toggleOpen();
    setChildrenLoaded(true);
  }

  return (
    <div
      className={classnames('lazy-content-collapse', className)}
      data-tracking-id={trackingId}
      data-tracking-value={trackingValue}
    >
      <AnimatedCollapse isOpen={isOpen && canOpen} noAnim={noAnim}>
        {state => (
          <div
            ref={mutationTarget}
            className={classnames('collapse-content', { 'fade-in': state === 'expanded' && isOpen && canOpen })}
          >
            {childrenLoaded ? children : null}
          </div>
        )}
      </AnimatedCollapse>

      <div className="collapse-control mb-0 row align-items-center mx-0 mb-1_5 justify-content-start">
        <Button
          color="link"
          className="content-collapse-button text-primary-darker size-16 p-0 pos-s"
          onClick={toggle}
          data-tracking-id={btnTrackingId ? `${btnTrackingId}${isOpen ? '_collapse' : '_expand'}` : null}
          aria-expanded={isOpen}
        >
          <span className="control-text text-primary-darker me-0_5">{isOpen ? textOpen : textClosed}</span>
          <span
            className={classnames('size-10 small text-primary-darker', {
              'icon-arrow-up4': isOpen,
              'icon-arrow-down4': !isOpen,
            })}
            aria-hidden
          />
        </Button>
      </div>
    </div>
  );
}

LazyContentCollapseTransitioned.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node, PropTypes.func]).isRequired,
  textClosed: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  textOpen: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  trackingId: PropTypes.string,
  trackingValue: PropTypes.string,
  btnTrackingId: PropTypes.string,
  noAnim: PropTypes.bool,
  className: PropTypes.string,
};

LazyContentCollapseTransitioned.defaultProps = {
  textClosed: 'Read more',
  textOpen: 'Read less',
  trackingId: null,
  trackingValue: null,
  btnTrackingId: null,
  noAnim: false,
  className: null,
};
